import * as THREE from 'three';
import { Bullet } from './Bullet';
import { Explosion } from './Explosion';
import type { EntityCtx } from '../Entity';

/**
 * A tank cannon shell — a real solid projectile (steel body + brass driving band + ogive
 * nose) that flies along its velocity leaving a thin smoke streak, hits hard, and bursts
 * with a proper detonation. Reuses the Bullet collision path (kind 'bullet') but carries
 * heavier damage and a bigger impact than an MG tracer.
 */
const _z = new THREE.Vector3(0, 0, 1);

export class Shell extends Bullet {
  private trailTimer = 0;

  constructor(pos: THREE.Vector3, vel: THREE.Vector3, ownerId: number, owner: 'player' | 'enemy' = 'player') {
    super(pos, vel, ownerId, owner);
    this.radius = 1.0;
    this.ttl = 3.0;
    this.damageAmount = 3;   // chunks enemy armour (4 HP) in two hits; meaningful vs bosses
    this.impactScale = 6;    // a real shell burst, not a spark

    // Replace the tracer sprite the base Bullet added with a solid shell model.
    this.group.clear();
    this.group.add(makeShellMesh());
    // Orient the shell along its flight path.
    this.group.quaternion.setFromUnitVectors(_z, vel.clone().normalize());
  }

  update(ctx: EntityCtx): void {
    super.update(ctx); // advance + age
    if (this.dead) return;
    // Smoke streak.
    this.trailTimer += ctx.dt;
    if (this.trailTimer >= 0.03) {
      this.trailTimer = 0;
      new Explosion(this.group.position.clone(), 4, 0.5, 'smoke')
        .setDrift(new THREE.Vector3(0, 2, 0)).addTo(ctx);
    }
  }
}

function makeShellMesh(): THREE.Group {
  const g = new THREE.Group();
  const steel = new THREE.MeshStandardMaterial({ color: 0x3a3a3e, roughness: 0.45, metalness: 0.7 });
  const brass = new THREE.MeshStandardMaterial({ color: 0xb08a3a, roughness: 0.5, metalness: 0.6 });
  // Body (along +Z).
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.7, 20, 1), steel);
  body.rotation.x = Math.PI / 2;
  g.add(body);
  // Brass driving band near the base.
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.12, 20, 1), brass);
  band.rotation.x = Math.PI / 2;
  band.position.z = 0.26;
  g.add(band);
  // Ogive nose.
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.34, 20, 1), steel);
  nose.rotation.x = -Math.PI / 2;
  nose.position.z = -0.52;
  g.add(nose);
  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return g;
}
